import React from "react";
import { SiteInfo } from "../SiteAboutPageUtils/siteAboutInterfaces";
import { addIgnoreLabel } from "../SiteAboutPageUtils/addIgnoreLabel";
import { useFlexIgnoreZonesOnboardingStore } from "./flexIgnoreZonesOnboardingStore";
import { useFlexIzScheduleSummary } from "./useFlexIzScheduleSummary";
import { FlexIgnoreZonesEditPresetCard } from "./FlexIgnoreZonesEditPresetCard";
import { FlexIgnoreZonesNewPresetDialog } from "./FlexIgnoreZonesNewPresetDialog";
import {
  collapseNoIgnoreZonesPresetOptions,
  filterFlexPresetsForApiPresetDropdown,
  normalizeFlexPresetSelectionToApiBackedMenuValue,
} from "./flexPresetDisplayDedupe";
import { FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION } from "./flexIzPresetSelectConstants";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import { findPresetConflicts, isFleetSite } from "./flexPresetConflicts";
import { FlexPresetConflictsNotice } from "./FlexPresetConflictsNotice";

export const FlexIgnoreZonesScheduleEditPresetSection = ({
  siteInfo,
  showFlexIgnoreZones,
  flexIgnoreZonesEnabled,
  flexSchedulesOn,
}: {
  siteInfo: SiteInfo;
  showFlexIgnoreZones: boolean;
  flexIgnoreZonesEnabled: boolean;
  flexSchedulesOn: boolean;
}) => {
  const userInfo = JSON.parse(localStorage.userInfo);
  const permissions = userInfo.permission;

  const {
    presetsByCamera,
    setSelectedPresetForScheduleCamera,
    createCustomPresetDraft,
    loadCameraContext,
    cameraContexts,
    selectedPresetByScheduleCamera,
  } = useFlexIgnoreZonesOnboardingStore();

  const { siteSchedules } = useSiteAboutStore();
  const presetNamesByCamera = useFlexIgnoreZonesOnboardingStore(
    (state) => state.presetNamesByCamera
  );
  const storeCameras = useFlexIgnoreZonesOnboardingStore(
    (state) => state.cameras
  );
  const fleetSite = isFleetSite(siteInfo);

  // ENG-1927: on a fleet site a camera can run one preset at a time. Save
  // Changes refuses these (blockOnFlexPresetConflicts); list them here so the
  // user can fix them before saving.
  const presetConflicts = React.useMemo(
    () =>
      fleetSite
        ? findPresetConflicts({
            schedules: siteSchedules || [],
            cameras: storeCameras,
            selectedPresetByScheduleCamera,
            presetsByCamera,
            presetNamesByCamera,
            identity: "persisted",
          })
        : [],
    [
      fleetSite,
      siteSchedules,
      storeCameras,
      selectedPresetByScheduleCamera,
      presetsByCamera,
      presetNamesByCamera,
    ]
  );

  const summary = useFlexIzScheduleSummary(siteInfo, {
    showFlexIgnoreZones,
    flexScheduleActive: flexSchedulesOn && permissions.flex_schedule,
    flexIgnoreZonesEnabled,
  });

  const [newPresetDialog, setNewPresetDialog] = React.useState<{
    scheduleId: number;
    cameraId: number;
    presetLocalId: string;
    cameraName: string;
  } | null>(null);

  if (!summary) {
    return null;
  }

  return (
    <>
      <div className="pl-[15px] pr-[20px] pt-[16px] pb-[24px] bg-white">
        <p className="font-mulish text-[18px] font-black pb-[12px]">
          Selected Cameras / Flex Schedule Presets
        </p>
        <FlexPresetConflictsNotice conflicts={presetConflicts} />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start max-w-[1500px]">
          {summary.rows.map(({ cameraId, camera, preset }) => {
            const allPresets = presetsByCamera[cameraId] || [];
            const rawSelectedId =
              selectedPresetByScheduleCamera[
                `${summary.resolvedScheduleId}:${cameraId}`
              ] || preset.localId;
            const selectedLocalId = normalizeFlexPresetSelectionToApiBackedMenuValue(
              cameraId,
              allPresets,
              rawSelectedId
            );
            const menuPresets = collapseNoIgnoreZonesPresetOptions(
              filterFlexPresetsForApiPresetDropdown(allPresets),
              selectedLocalId
            );
            // An assignment the zone-bundle list does not carry still shows up
            // (with its real name) instead of leaving the select out of range.
            const selectablePresets =
              preset.localId !== FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION &&
              !menuPresets.some((p) => p.localId === preset.localId)
                ? [...menuPresets, preset]
                : menuPresets;
            const cameraContext = cameraContexts[cameraId];
            // "Default" previews the camera's own zones (the loaded context is
            // fresher than the site camera list's `ignore_zones`).
            const presetForCard =
              preset.localId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION &&
              (cameraContext?.defaultZones?.length ?? 0) > 0
                ? { ...preset, zones: cameraContext.defaultZones }
                : preset;

            return (
              <FlexIgnoreZonesEditPresetCard
                key={`${summary.resolvedScheduleId}-${cameraId}`}
                scheduleId={summary.resolvedScheduleId}
                camera={camera}
                preset={presetForCard}
                cameraContext={cameraContext}
                selectablePresets={selectablePresets}
                selectedPresetLocalId={selectedLocalId}
                onSelectPreset={(presetLocalId) => {
                  setSelectedPresetForScheduleCamera(
                    summary.resolvedScheduleId,
                    cameraId,
                    presetLocalId
                  );
                  loadCameraContext(cameraId);
                }}
                onCreateNewPreset={() => {
                  const ctxZones = cameraContexts[cameraId]?.defaultZones;
                  const zones =
                    ctxZones && ctxZones.length > 0
                      ? ctxZones
                      : addIgnoreLabel(camera.ignore_zones || []);
                  const localId = createCustomPresetDraft(cameraId, zones);
                  setNewPresetDialog({
                    scheduleId: summary.resolvedScheduleId,
                    cameraId,
                    presetLocalId: localId,
                    cameraName: camera.camera_name,
                  });
                  loadCameraContext(cameraId);
                }}
                onPreviewImageError={() => {
                  loadCameraContext(cameraId, true);
                }}
                showNoIgnoreZonesOption={false}
                showDefaultCameraConfigOption
              />
            );
          })}
        </div>
      </div>

      <FlexIgnoreZonesNewPresetDialog
        open={newPresetDialog != null}
        scheduleId={newPresetDialog?.scheduleId ?? 0}
        cameraId={newPresetDialog?.cameraId ?? 0}
        presetLocalId={newPresetDialog?.presetLocalId ?? null}
        cameraName={newPresetDialog?.cameraName ?? ""}
        assignToSchedule={false}
        onClose={(reason, meta) => {
          if (reason === "submitted" && newPresetDialog) {
            loadCameraContext(newPresetDialog.cameraId, true);
            if (meta?.presetLocalId) {
              setSelectedPresetForScheduleCamera(
                newPresetDialog.scheduleId,
                newPresetDialog.cameraId,
                meta.presetLocalId
              );
            }
          }
          setNewPresetDialog(null);
        }}
      />
    </>
  );
};
